import { Input, Label } from '@hqo/ui-atoms';
import type { FieldError, FieldPath, UseFormRegister } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';

interface InputFieldProps {
  id: string;
  name: FieldPath<CreateVisitFormV2Values>;
  register: UseFormRegister<CreateVisitFormV2Values>;
  error?: FieldError;
  value?: string;

  labelKey: string;
  labelDefault: string;
  required?: boolean;

  type?: 'text' | 'email';
  placeholderKey: string;
  placeholderDefault: string;

  hint?: {
    key: string;
    default: string;
  };
  className?: string;
  maxLength?: number;
  disabled?: boolean;
}

export const InputField = ({
  id,
  name,
  register,
  error,
  value,
  labelKey,
  labelDefault,
  required = false,
  type = 'text',
  placeholderKey,
  placeholderDefault,
  hint,
  className,
  maxLength,
  disabled,
}: InputFieldProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();
  const showError = Boolean(error);
  const currentLength = value?.length || 0;
  const inputClassName = [className, showError ? 'border-destructive' : ''].filter(Boolean).join(' ');
  let helperContent: JSX.Element | null = null;
  if (showError) {
    helperContent = <p className="text-sm text-destructive">{formatValidationMessage(error?.message)}</p>;
  } else if (hint) {
    helperContent = (
      <p className="text-xs text-gray-500">{formatMessage({ id: hint.key, defaultMessage: hint.default })}</p>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-foreground gap-1">
        {formatMessage({ id: labelKey, defaultMessage: labelDefault })}
        {required && <span className="text-destructive">*</span>}
      </Label>

      <Input
        id={id}
        type={type}
        placeholder={formatMessage({ id: placeholderKey, defaultMessage: placeholderDefault })}
        className={inputClassName}
        maxLength={maxLength}
        data-cy={id}
        disabled={disabled}
        {...register(name)}
      />

      {(helperContent || maxLength) && (
        <div className="flex justify-between items-center min-h-[20px]">
          {helperContent ?? <span />}
          {maxLength && (
            <p className={`text-xs ${currentLength > maxLength ? 'text-destructive' : 'text-gray-400'}`}>
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
